import React, { useRef, useState } from 'react';
import { Pills } from '@saleshandy/design-system';

import Input from '../../../../../../../shared/design-system/components/input';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { truncate } from '../../../../../../../shared/utils';

type IProps = {
  value: string[];
  onUpdateFilterConditionValue: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
  validate?: (value: string[]) => boolean;
};

const ProspectFiltersTextInput: React.FC<IProps> = ({
  value = [],
  onUpdateFilterConditionValue,
  disabled = false,
  className = '',
  validate,
}) => {
  const visibleCount = 2;
  const emailInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [localInputValue, setLocalInputValue] = useState('');

  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e: React.FocusEvent) => {
    // Only blur if focus is moving outside the container
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
    }
  };

  const onAddText = () => {
    // Split input by comma, remove whitespace, and filter out empty values
    const addedText = localInputValue
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    // Filter out items that already exist in `value`
    const uniqueAddedText = addedText.filter((item) => !value.includes(item));

    const addValue = () => {
      const updatedValue = [...(value || []), ...uniqueAddedText];
      onUpdateFilterConditionValue(updatedValue);
      // Clear the input after processing
      setLocalInputValue('');
    };

    // Only update if there are new unique items
    if (uniqueAddedText.length > 0) {
      if (validate) {
        if (validate(uniqueAddedText)) {
          addValue();
        }
      } else {
        addValue();
      }
    }
  };

  const onRemoveText = (index: number) => {
    const updatedValue = value.filter((_, i) => i !== index);
    onUpdateFilterConditionValue(updatedValue);
  };

  const onAction = (key: string) => {
    if (key === 'Enter' || key === 'Blur') {
      onAddText();
      return;
    }

    if (key === 'Backspace') {
      if (value.length > 0 && !localInputValue) {
        onRemoveText(value.length - 1);
      }
    }
  };

  const onContainerClicked = () => {
    if (emailInputRef?.current) {
      emailInputRef.current.focus();
      handleFocus();
    }
  };

  const onKeyPress = (e) => {
    onAction(e.key);
  };

  const onBlur = () => {
    onAction('Blur');
  };

  return (
    <div
      ref={containerRef}
      className={`prospect-filters-text-input-container ${className} ${
        isFocused || value?.length < visibleCount ? 'focused' : ''
      }`}
      {...accessibleOnClick(onContainerClicked)}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className="prospect-filters-text-input-pills">
        {isFocused
          ? value?.map((text, index) => (
              <Pills
                theme="outline"
                size="md"
                label={truncate(text || '', 36, false)}
                showCloseIcon
                onClose={() => {
                  if (!disabled) {
                    onRemoveText(index);
                  }
                }}
                key={text}
              />
            ))
          : (() => {
              const remainingCount = value.length - visibleCount;

              return (
                <>
                  {value.slice(0, visibleCount).map((text, index) => (
                    <Pills
                      theme="outline"
                      size="md"
                      label={truncate(text || '', 10, false)}
                      showCloseIcon
                      onClose={() => {
                        if (!disabled) {
                          onRemoveText(index);
                        }
                      }}
                      key={text}
                    />
                  ))}
                  {remainingCount > 0 && (
                    <Pills
                      theme="outline"
                      size="md"
                      label={`+${remainingCount}`}
                      key="remaining"
                    />
                  )}
                </>
              );
            })()}

        <Input
          value={localInputValue}
          onChange={(updatedValue) => {
            setLocalInputValue(updatedValue);
          }}
          autoFocus
          placeholder="Enter text"
          ref={emailInputRef}
          onKeyUp={onKeyPress}
          onFocus={handleFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default ProspectFiltersTextInput;
